export type DailyUsage = {
  value: number
  date: string
  metadata?: {
    botId: string
    botName?: string
    type: string
    subtype: string
    source?: string
  }
}

export type DailySummary = {
  date: string
  value: number
  dailyAverage?: number
  bots: { name: string; value: number }[]
  sources: { name: string; value: number }[]
  types: { name: string; value: number }[]
  subtypes: { name: string; value: number }[]
  projectedCumulative?: number
  cumulative?: number
}

export type FlattenedDailySummary = Record<string, string | number>

/**
 * Merges two arrays of DailyUsage entries into a compact array of per-day summaries.
 *
 * Each summary aggregates total usage for a given date, along with breakdowns
 * by botId, source, type, and subtype. All entries with the same date and matching
 * metadata fields are grouped together. The final result contains at most one item
 * per date.
 *
 * @param usage1 - First array of usage entries
 * @param usage2 - Second array of usage entries
 * @returns An array of DailySummary objects, each representing a single date with totals and breakdowns
 */
export const mergeDailyUsages: (usage1: DailyUsage[], usage2: DailyUsage[]) => DailySummary[] = (usage1, usage2) => {
  const mergedUsage = new Map()

  // Combine both usage arrays and process them in a single loop
  ;[...usage1, ...usage2].forEach(({ date, value, metadata }) => {
    const acc = mergedUsage.get(date) ?? {
      value: 0,
      bots: new Map<string, number>(),
      sources: new Map<string, number>(),
      types: new Map<string, number>(),
      subtypes: new Map<string, number>(),
    }

    // Accumulate the total value for the date
    acc.value += value

    // Accumulate values by botId, source, type, and subtype
    if (metadata?.botName) {
      acc.bots.set(metadata.botName, (acc.bots.get(metadata.botName) || 0) + value)
    }
    if (metadata?.source) {
      acc.sources.set(metadata.source, (acc.sources.get(metadata.source) || 0) + value)
    }
    if (metadata?.type) {
      acc.types.set(metadata.type, (acc.types.get(metadata.type) || 0) + value)
    }
    if (metadata?.subtype) {
      acc.subtypes.set(metadata.subtype, (acc.subtypes.get(metadata.subtype) || 0) + value)
    }

    mergedUsage.set(date, acc)
  })

  // Convert the map back to an array
  return Array.from(mergedUsage, ([date, acc]) => ({
    date,
    value: acc.value,
    bots: Array.from(acc.bots, ([name, value]) => ({ name, value })),
    sources: Array.from(acc.sources, ([name, value]) => ({ name, value })),
    types: Array.from(acc.types, ([name, value]) => ({ name, value })),
    subtypes: Array.from(acc.subtypes, ([name, value]) => ({ name, value })),
  }))
}

/**
 * Ensures data exists for every day from the start of the month up to either:
 * - The current day (if viewing the current month)
 * - The last day (if viewing a past month)
 *
 * This function:
 * 1. Takes existing data points and preserves their values
 * 2. Fills in missing dates with zero values
 * 3. Does not add data for future dates
 * 4. Returns a chronologically sorted array with one entry per day
 *
 * This is useful for charts to ensure a continuous date range is displayed
 * with appropriate values, even when the original data has gaps.
 *
 * @param data The original sparse data array with dates and values
 * @param selectedMonth The month being displayed
 * @returns A complete array of data points for the selected month up to the current day
 */
export const fillMissingDates = (data: DailySummary[], selectedMonth: Date): DailySummary[] => {
  const filledData: DailySummary[] = []
  const today = new Date()
  today.setHours(0, 0, 0, 0) // Reset time component for accurate comparison

  // Calculate the last day to include (either today or end of month)
  const isCurrentMonth =
    today.getMonth() === selectedMonth.getMonth() && today.getFullYear() === selectedMonth.getFullYear()

  // If it's the current month, we only go up to today
  // Otherwise, we include the entire month
  const lastDayOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0).getDate()
  const lastDay = isCurrentMonth ? Math.min(today.getDate(), lastDayOfMonth) : lastDayOfMonth

  // Fill data from day 1 to the calculated last day
  for (let day = 1; day <= lastDay; day++) {
    const date = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), day)
      .toISOString()
      .split('T')[0] as string
    const existingEntry = data.find((entry) => entry.date === date)
    filledData.push(existingEntry || { date, value: 0, bots: [], sources: [], types: [], subtypes: [] })
  }

  return filledData
}

export const fillMissingDatesFromDateRange = (data: DailySummary[], startDate: Date, endDate: Date): DailySummary[] => {
  const dataMap = new Map(data.map((item) => [item.date, item]))
  const filledData: DailySummary[] = []

  const current = new Date(startDate)
  while (current <= endDate) {
    const dateString = current.toISOString().split('T')[0] ?? ''

    if (dataMap.has(dateString)) {
      filledData.push(dataMap.get(dateString)!)
    } else {
      filledData.push({
        date: dateString,
        value: 0,
        bots: [],
        sources: [],
        types: [],
        subtypes: [],
      })
    }

    current.setDate(current.getDate() + 1)
  }

  return filledData
}

export function addDailyAverage(
  data: FlattenedDailySummary[],
  selectedMonth: Date,
  fillValue: number
): FlattenedDailySummary[] {
  // get the last day of the month
  const dataCopy = [...data]
  const lastDayOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0).getDate()

  for (let day = 1; day <= lastDayOfMonth; day++) {
    const date = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), day)
      .toISOString()
      .split('T')[0] as string

    // Check if date already exists in the array
    const existingIndex = dataCopy.findIndex((item) => item.date === date)

    if (existingIndex !== -1 && dataCopy[existingIndex]) {
      // Update existing entry with projected value
      dataCopy[existingIndex] = {
        ...dataCopy[existingIndex],
        dailyAverage: fillValue,
      }
    } else {
      // Add new entry if date doesn't exist
      dataCopy.push({ date, dailyAverage: fillValue })
    }
  }

  return dataCopy
}

type Breakdown = {
  id: string
  value: number
  name?: string
}
/**
 * Segregates an array of breakdowns into top n breakdowns and a sum of the rest.
 *
 * @param breakdowns - The array of Breakdown objects to segregate
 * @param n - The number of top breakdowns to keep (default: 3)
 * @returns An object containing:
 *   - usages: Array of the top n breakdowns by value
 *   - remainer: Sum of all remaining breakdowns' values, or undefined if no remaining usage
 */
export function segregateUsages(
  breakdowns: Breakdown[],
  n = 3
): {
  usages: Breakdown[]
  remainder?: number
} {
  const sortedBreakdowns = [...breakdowns].sort((a, b) => b.value - a.value)

  const usages = sortedBreakdowns.slice(0, n)
  const remainder =
    sortedBreakdowns.length > n ? sortedBreakdowns.slice(n).reduce((acc, { value }) => acc + value, 0) : undefined

  return {
    usages,
    remainder,
  }
}

/**
 * Flattens a breakdown from DailySummary entries into a Recharts-compatible array,
 * including both per-item daily values and cumulative values.
 *
 * For a given breakdown type (e.g. 'bots', 'sources', 'types', or 'subtypes'), this function
 * converts the structured summary data into flat objects, where each breakdown item (bot, source, etc.)
 * produces two keys per day: `${id}_value` and `${id}_cumulative`. Missing values default to 0.
 *
 * It also includes `projectedCumulative`, `cumulative` and `dailyAverage` from the summary for global trend overlays.
 *
 * Example output:
 * [
 *   { date: '2025-06-01', dailyAverage: 12, cumulative: 10, projectedCumulative: 10, bot1_value: 10, bot1_cumulative: 10 },
 *   { date: '2025-06-02', dailyAverage: 12, cumulative: 15, projectedCumulative: 20, bot1_value: 5,  bot1_cumulative: 15 },
 *   ...
 * ]
 *
 * @param summaries - Array of DailySummary objects (with projectedCumulative)
 * @param key - The breakdown field to flatten ('bots' | 'sources' | 'types' | 'subtypes')
 * @returns An array of flat records, one per day, with per-id value and cumulative keys
 */
export function flattenBreakdownWithCumulative(
  summaries: DailySummary[],
  key: 'bots' | 'sources' | 'types' | 'subtypes'
): FlattenedDailySummary[] {
  const allIds = new Set<string>()

  for (const summary of summaries) {
    for (const item of summary[key]) {
      allIds.add(item.name)
    }
  }

  const cumulativeMap = new Map<string, number>()
  let otherCumulative = 0

  return summaries.map((summary) => {
    const row: FlattenedDailySummary = {
      date: summary.date,
      projectedCumulative: summary.projectedCumulative ?? 0,
      cumulative: summary.cumulative ?? 0,
      dailyAverage: summary.dailyAverage ?? 0,
    }

    let totalValueFromBreakdown = 0

    for (const id of allIds) {
      const item = summary[key].find((i) => ('id' in i ? i.id : i.name) === id)
      const value = item?.value ?? 0

      const cumulative = (cumulativeMap.get(id) ?? 0) + value
      cumulativeMap.set(id, cumulative)

      row[`${id}_value`] = value
      row[`${id}_cumulative`] = cumulative

      totalValueFromBreakdown += value
    }

    const otherValue = Math.max(0, summary.value - totalValueFromBreakdown)
    otherCumulative += otherValue

    row['Other_value'] = otherValue
    row['Other_cumulative'] = otherCumulative

    return row
  })
}

/**
 * Reduces flattened daily usage data to only include the top N bots,
 * grouping the rest into an "Other" category. The top bots are determined
 * by their cumulative usage on the final day, and must also meet a minimum
 * percentage of total usage to qualify.
 *
 * Ensures that "Other" is always included and appears last in the visible bot list.
 * Preserves `date`, `projectedCumulative`, `cumulative`, and `dailyAverage` fields.
 *
 * @param data - The flattened daily usage array (from `flattenBreakdownWithCumulative`)
 * @param topN - Maximum number of top [bots, sources or subtypes] to include explicitly (default: 5)
 * @param minPercent - Minimum percentage (0–1) of total usage a cumulative must have to be shown (default: 0.05)
 * @returns An object with:
 *  - `grouped`: the updated daily data with unused cumulatives collapsed into "Other"
 *  - `visibleKeys`: array of [bot IDs, sources, subtypes] used in the output (including "Other" as last item)
 */
export function collapseDailyUsageToTopN(
  data: Record<string, any>[],
  topN: number = 5,
  minPercent: number = 0.05
): { grouped: Record<string, any>[]; visibleKeys: string[] } {
  if (data.length === 0) {
    return { grouped: [], visibleKeys: [] }
  }

  const lastRow = data[data.length - 1]!

  // Step 1: extract all cumulative values
  const allCumulativeEntries = Object.entries(lastRow).filter(([key]) => key.endsWith('_cumulative'))

  const totals = allCumulativeEntries.map(([key, value]) => ({
    id: key.replace('_cumulative', ''),
    value: value as number,
  }))

  // Include 'other' in total calculation
  const total = totals.reduce((sum, b) => sum + b.value, 0)

  // Step 2: exclude 'other' from topN logic, and ensure topN values are above minPercent usage
  const top = totals
    .filter((b) => b.id !== 'Other' && b.value / total >= minPercent)
    .sort((a, b) => b.value - a.value)
    .slice(0, topN)
    .map((b) => b.id)

  const visibleKeys = top.concat('Other')

  // Step 3: collapse all non-top values into 'other'
  const grouped = data.map((row) => {
    const newRow: Record<string, any> = {
      date: row.date,
      projectedCumulative: row.projectedCumulative,
      cumulative: row.cumulative,
      dailyAverage: row.dailyAverage,
    }

    for (const [key, value] of Object.entries(row)) {
      const parts = key.split('_')
      const type = parts.pop()
      const id = parts.join('_')

      // Skip keys such as date, projectedCumulative, cumulative, dailyAverage
      if (!id || !type) continue

      if (visibleKeys.includes(id)) {
        newRow[key] = (newRow[key] ?? 0) + (value as number)
      } else {
        const otherKey = `Other_${type}`
        newRow[otherKey] = (newRow[otherKey] ?? 0) + (value as number)
      }
    }

    return newRow
  })

  // Step 4: remove 'Other' if it has no cumulative value
  const shouldOmitOther = grouped[grouped.length - 1]?.Other_cumulative === 0
  if (shouldOmitOther) {
    for (const row of grouped) {
      delete row.Other_value
      delete row.Other_cumulative
    }
    const otherIndex = visibleKeys.indexOf('Other')
    if (otherIndex !== -1) {
      visibleKeys.splice(otherIndex, 1)
    }
  }

  return { grouped, visibleKeys }
}

/**
 * Extracts and returns all cumulative metrics with values > 0 from the latest summary entry.
 *
 * @param data Array of FlattenedDailySummary objects
 * @returns An array of objects with id, name, and numeric value for each '_cumulative' key found.
 */
export const getBreakdown = (data: FlattenedDailySummary[]) => {
  return Object.entries(data[data.length - 1] || {})
    .filter(([key]) => key.endsWith('_cumulative'))
    .map(([key, value]) => {
      const id = key.replace('_cumulative', '')
      return {
        id,
        name: id,
        value: Number(value),
      }
    })
    .filter(({ value }) => value > 0)
    .sort((a, b) => b.value - a.value)
}

export const addProjectedCumulative = (
  sortedData: DailySummary[],
  projection: FlattenedDailySummary[],
  averagePerDay: number
): FlattenedDailySummary[] =>
  projection.map((item, index) => ({
    ...item,
    ...(item.value !== undefined
      ? {
          cumulative: sortedData.slice(0, index + 1).reduce((acc, { value }) => acc + (value ?? 0), 0),
        }
      : {}),
    projectedCumulative: (index + 1) * averagePerDay,
  }))
